import { useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, Lock, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import AuthLayout from "./AuthLayout";
import { adminApi, formatApiError } from "../../lib/authClient";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 8) return toast.error("Password must be at least 8 characters.");
    if (password !== confirm) return toast.error("Passwords do not match.");
    setLoading(true);
    try {
      await adminApi.post("/auth/reset-password", { token, new_password: password });
      toast.success("Password updated! Please sign in.");
      navigate("/admin/login", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Reset failed");
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <AuthLayout title="Invalid link">
        <div className="text-center" data-testid="reset-invalid">
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-white/10">
            <AlertCircle size={34} className="text-white/70" />
          </div>
          <p className="mt-6 text-white/70">
            This reset link is invalid or incomplete.
          </p>
          <Link
            to="/admin/forgot-password"
            className="mt-6 inline-block text-white hover:underline"
          >
            Request a new link
          </Link>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title="Set New Password" subtitle="Choose a strong new password for your account.">
      <form onSubmit={submit} data-testid="reset-form" className="space-y-4">
        <div className="relative">
          <Lock size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/65" />
          <input
            data-testid="reset-password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="New password (min 8 chars)"
            className="input-dark pl-11"
          />
        </div>
        <div className="relative">
          <Lock size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/65" />
          <input
            data-testid="reset-confirm"
            type="password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            placeholder="Confirm new password"
            className="input-dark pl-11"
          />
        </div>
        <motion.button
          type="submit"
          disabled={loading}
          whileTap={{ scale: 0.97 }}
          data-testid="reset-submit"
          className="flex w-full items-center justify-center gap-2 rounded-2xl bg-white px-6 py-4 font-semibold text-black transition-colors hover:bg-white/90 disabled:opacity-60"
        >
          {loading ? <Loader2 size={18} className="animate-spin" /> : "Update Password"}
        </motion.button>
      </form>
    </AuthLayout>
  );
}
